import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import {
  PRICE_COLUMNS,
  PRICE_COLUMN_LABELS,
  PriceColumn,
  ShippingPriceInput,
  ShippingPriceType,
} from "types/ShippingTypes";
import {
  apiCreateShippingPrice,
  apiDeleteShippingPrice,
  apiGetShippingPrices,
  apiUpdateShippingPrice,
} from "util/network/Shipping";
import {
  ShipmentPage,
  errorText,
  formatDate,
  formatKr,
  useIsManager,
} from "./common";

/** One editable row, as typed. */
type Draft = {
  id: number | null;
  column: PriceColumn;
  weightFromKg: string;
  weightToKg: string;
  price: string;
  validFrom: string;
  validTo: string;
  expectedCost: string;
};

const toDraft = (row: ShippingPriceType): Draft => ({
  id: row.id,
  column: row.column,
  weightFromKg: String(row.weightFromKg),
  weightToKg: String(row.weightToKg),
  price: String(row.price),
  validFrom: row.validFrom ?? "",
  validTo: row.validTo ?? "",
  expectedCost: row.expectedCost == null ? "" : String(row.expectedCost),
});

const emptyDraft = (column: PriceColumn): Draft => ({
  id: null,
  column,
  weightFromKg: "",
  weightToKg: "",
  price: "",
  validFrom: "",
  validTo: "",
  expectedCost: "",
});

const number = (value: string): number => Number(value.replace(",", "."));

const toInput = (draft: Draft): ShippingPriceInput | null => {
  const from = number(draft.weightFromKg);
  const to = number(draft.weightToKg);
  const price = number(draft.price);
  const expected =
    draft.expectedCost.trim() === "" ? null : number(draft.expectedCost);
  if (
    draft.weightFromKg.trim() === "" ||
    draft.weightToKg.trim() === "" ||
    draft.price.trim() === "" ||
    ![from, to, price].every(Number.isFinite) ||
    (expected !== null && !Number.isFinite(expected))
  ) {
    return null;
  }
  return {
    column: draft.column,
    weightFromKg: from,
    weightToKg: to,
    price,
    validFrom: draft.validFrom || null,
    validTo: draft.validTo || null,
    expectedCost: expected,
  };
};

const isActive = (row: ShippingPriceType) => {
  const today = dayjs().format("YYYY-MM-DD");
  return (
    (!row.validFrom || row.validFrom <= today) &&
    (!row.validTo || row.validTo >= today)
  );
};

/** The price table the webshop and the quotes charge from (ex VAT, per weight
 *  band, a row per column). The checkout picks the band where
 *  from < weight <= to. */
const ShippingPrices: React.FC = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const isManager = useIsManager();
  const [column, setColumn] = useState<PriceColumn>("gls");
  const [rows, setRows] = useState<ShippingPriceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShippingPriceType | null>(
    null
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    apiGetShippingPrices(token)
      .then(setRows)
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setLoading(false));
  }, [token]);

  const save = () => {
    const input = draft && toInput(draft);
    if (!draft || !input) return;
    setSaving(true);
    const request =
      draft.id === null
        ? apiCreateShippingPrice(token, input)
        : apiUpdateShippingPrice(token, draft.id, input);
    request
      .then((saved) => {
        setRows((current) =>
          draft.id === null
            ? [...current, saved]
            : current.map((row) => (row.id === saved.id ? saved : row))
        );
        setDraft(null);
        addToast({ type: "success", message: "Price saved" });
      })
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setSaving(false));
  };

  const remove = () => {
    const target = deleteTarget;
    if (!target) return;
    setSaving(true);
    apiDeleteShippingPrice(token, target.id)
      .then(() => {
        setRows((current) => current.filter((row) => row.id !== target.id));
        setDeleteTarget(null);
        addToast({ type: "success", message: "Price deleted" });
      })
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setSaving(false));
  };

  const visible = rows
    .filter((row) => row.column === column)
    .sort(
      (a, b) =>
        a.weightFromKg - b.weightFromKg ||
        (a.validFrom ?? "").localeCompare(b.validFrom ?? "")
    );

  const field = (key: keyof Draft, label: string, type = "text") =>
    draft && (
      <TextField
        size="small"
        label={label}
        type={type}
        value={draft[key] ?? ""}
        onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
        InputLabelProps={type === "date" ? { shrink: true } : undefined}
        inputProps={type === "text" ? { inputMode: "decimal" } : undefined}
      />
    );

  return (
    <ShipmentPage
      title="Shipping prices"
      actions={
        isManager && (
          <Button
            startIcon={<AddIcon />}
            sx={{ color: "#fff" }}
            onClick={() => setDraft(emptyDraft(column))}
          >
            Add band
          </Button>
        )
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Prices are ex VAT. A band covers weights above "from" up to and
        including "to". Above the weight limit in the shipment settings, the
        checkout shows "price on request". Bands in the same column may not
        overlap in weight while their dates overlap.
      </Alert>
      <Tabs
        value={column}
        onChange={(_, value) => setColumn(value)}
        sx={{ mb: 2 }}
      >
        {PRICE_COLUMNS.map((value) => (
          <Tab key={value} value={value} label={PRICE_COLUMN_LABELS[value]} />
        ))}
      </Tabs>
      {loading ? (
        <CircularProgress />
      ) : visible.length === 0 ? (
        <Typography color="text.secondary">No bands in this column.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Weight (kg)</TableCell>
                <TableCell>Price ex VAT</TableCell>
                <TableCell>Incl. VAT</TableCell>
                <TableCell>Expected cost</TableCell>
                <TableCell>Valid</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ opacity: isActive(row) ? 1 : 0.55 }}
                >
                  <TableCell>
                    {row.weightFromKg} - {row.weightToKg}
                  </TableCell>
                  <TableCell>{formatKr(row.price)}</TableCell>
                  <TableCell>{formatKr(row.priceInclTax)}</TableCell>
                  <TableCell>{formatKr(row.expectedCost)}</TableCell>
                  <TableCell>
                    {row.validFrom || row.validTo
                      ? `${formatDate(row.validFrom) || "…"} - ${
                          formatDate(row.validTo) || "…"
                        }`
                      : "Always"}
                  </TableCell>
                  <TableCell align="right">
                    {isManager && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          size="small"
                          onClick={() => setDraft(toDraft(row))}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(row)}
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={!!draft}
        onClose={() => setDraft(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {draft?.id === null ? "Add band" : "Edit band"}
        </DialogTitle>
        <DialogContent>
          {draft && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                pt: 1,
              }}
            >
              <FormControl size="small">
                <InputLabel>Column</InputLabel>
                <Select
                  label="Column"
                  value={draft.column}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      column: event.target.value as PriceColumn,
                    })
                  }
                >
                  {PRICE_COLUMNS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {PRICE_COLUMN_LABELS[value]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {field("price", "Price ex VAT (kr)")}
              {field("weightFromKg", "From (kg, exclusive)")}
              {field("weightToKg", "To (kg, inclusive)")}
              {field("validFrom", "Valid from", "date")}
              {field("validTo", "Valid to", "date")}
              {field("expectedCost", "Expected cost (kr, optional)")}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={saving || !draft || !toInput(draft)}
            onClick={save}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete band</DialogTitle>
        <DialogContent>
          {deleteTarget &&
            `${PRICE_COLUMN_LABELS[deleteTarget.column]} ${
              deleteTarget.weightFromKg
            }-${deleteTarget.weightToKg} kg, ${formatKr(
              deleteTarget.price
            )}. Orders already placed keep the price they were charged.`}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Keep</Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={remove}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </ShipmentPage>
  );
};

export default ShippingPrices;
